import { useAuth0 } from "@auth0/auth0-react";
import { datadogRum } from "@datadog/browser-rum";
import { ApiStatusToFileStatus } from "@utils/constants";
import { convertHeadersToObject } from "@utils/utils";

import { FileUploadAndParseProps } from "@fronterahealth/frontera-ui-components";

import {
  ApiLearnerFileStatusChoices,
  FileTypeEnum,
  LearnerFileByIdDocument,
  LearnerFileByIdQuery,
  useCreateIndependentLearnerFileMutation,
  useCreateLearnerMutation,
  useDeleteLearnerFileMutation,
  useMarkLearnerFileAsUploadedMutation,
} from "@api/graphql/types-and-hooks";
import { FronteraGraphQLClient } from "@api/graphql/useCustomFetcher";
import { notifyError, notifySuccess } from "@components/notifications/notifications";

// Shared intake file upload hooks - used by both BHTIA and ABC Intake flows

export const useIntakeS3BucketUrlRetriever: (
  setLearnerId: (learnerId: string | undefined) => void,
) => FileUploadAndParseProps["S3BucketUrlRetriever"] = (setLearnerId) => {
  const createLearnerMutation = useCreateLearnerMutation();
  const createIndependentLearnerFileMutation = useCreateIndependentLearnerFileMutation();

  return async ({ fileKind, fileName, mimeType, fileSize }) => {
    console.debug(
      `%c<FILE-API> | Requesting S3 URL | fileKind: ${fileKind} fileName: ${fileName} mimeType: ${mimeType} fileSize: ${fileSize}`,
      "color: black; background: red",
    );
    let learnerId: string | undefined;
    try {
      const createLearnerDataResponse = await createLearnerMutation.mutateAsync({});

      datadogRum.addAction("create-learner", {});
      console.debug(
        `<FILE-API> | S3BucketURLRetriever | Received Create Learner response  | ` +
          `%c${JSON.stringify(createLearnerDataResponse)}`,
        "color:red;",
      );

      learnerId = createLearnerDataResponse.createLearner?.learner?.id;
      setLearnerId(learnerId);
      if (!learnerId) {
        datadogRum.addError("CreateLearner mutation failed: No learner ID returned", {
          response: createLearnerDataResponse,
        });
        console.error("CreateLearner mutation failed", createLearnerDataResponse);
        throw new Error("Failed to create learner.");
      }
    } catch (error) {
      setLearnerId(undefined);
      console.error(
        `<FILE-API> | S3BucketURLRetriever | ERROR Failed to create learner | ` + `%c${JSON.stringify(error)}`,
        "color:red;",
      );
      throw new Error(String(error));
    }

    try {
      const createFileResponse = await createIndependentLearnerFileMutation.mutateAsync({
        input: {
          learnerId: learnerId,
          fileKind: fileKind as FileTypeEnum,
          fileSize,
          mimeType,
          originalFileName: fileName,
        },
      });

      const learnerFile = createFileResponse?.createIndependentLearnerFile?.learnerFile;

      datadogRum.addAction("create-learner-file", {
        fileMetadata: {
          fileKind,
          mimeType,
          fileName,
          fileId: learnerFile?.id,
        },
      });

      console.debug(
        `<FILE-API> | S3BucketURLRetriever | Received following bucket information | ` +
          `%c${JSON.stringify(createFileResponse)}`,
        "color:red;",
      );
      const bucket = learnerFile?.signedS3Url || "<missing-bucket-id>";
      const fileId = learnerFile?.id || "<missing-file-id>";

      return {
        bucket,
        fileId,
      };
    } catch (err) {
      console.error(
        `<FILE-API> | S3BucketURLRetriever | ERROR Received following bucket information | ` +
          `%c${JSON.stringify(err)}`,
        "color:red;",
      );
      throw new Error(String(err));
    }
  };
};

export const useGetIntakeS3BucketUploadCallback: () => FileUploadAndParseProps["S3BucketUploadCallback"] = () => {
  return async ({ file, mimeType, url }) => {
    console.debug(
      `%c<FILE-API> | Uploading file to S3 | fileName: ${file.name} mimeType: ${mimeType} url: ${url}`,
      "color: black; background: red",
    );
    try {
      const response = await fetch(url, {
        method: "PUT",
        headers: new Headers({ "Content-Type": mimeType }),
        body: file,
      });

      if (response.status === 200) {
        console.debug(`<FILE-API> | S3BucketUploadCallback | Done sending data to S3 | response: ${response}`);
        datadogRum.addAction("learner-file-uploaded-successfully", {
          fileMetadata: {
            fileName: file.name,
            mimeType,
          },
        });
        return;
      } else {
        console.error(`<FILE-API> | S3BucketUploadCallback | Error in sending data | response: ${response}`);
        throw new Error(`Non 200 error message: ${response.status} ${response.statusText}`);
      }
    } catch (err) {
      console.error("Error calling S3BucketUploadCallback", err);
      throw new Error(String(err));
    }
  };
};

type MetadataCreationMutation = {
  mutateAsync: (variables: { learnerFileId: string }) => Promise<unknown>;
};

export const useGetIntakeFileUploadCompleteCallback: (
  metadataCreationMutation: MetadataCreationMutation,
  logPrefix: string,
) => FileUploadAndParseProps["reportFileUploadCompleteCallback"] = (metadataCreationMutation, logPrefix) => {
  const markLearnerFileAsUploadedMutation = useMarkLearnerFileAsUploadedMutation({
    throwOnError: false,
  });
  return async (fileId) => {
    console.debug(
      `<FILE-API> | reportFileUploadCompleteCallback | Reporting file upload complete to FDM | ` +
        `%cfileId: ${fileId}`,
      "color:red;",
    );

    try {
      const setUploadedStatusResponse = await markLearnerFileAsUploadedMutation.mutateAsync({
        fileId,
      });

      datadogRum.addAction("mark-learner-file-as-uploaded", {
        fileMetadata: {
          fileId,
        },
      });

      const status = setUploadedStatusResponse?.markLearnerFileAsUploaded?.learnerFile?.status;
      console.debug(
        `<FILE-API> | reportFileUploadCompleteCallback | Marked learner file as uploaded in FDM | status: ${status}`,
      );

      console.debug(
        `<FILE-API> | ${logPrefix} | Reporting to start file parsing to FDM | ` + `%cfileId: ${fileId}`,
        "color:red;",
      );

      await metadataCreationMutation.mutateAsync({
        learnerFileId: fileId,
      });

      console.debug(`<FILE-API> | ${logPrefix} | Learner Metadata creation has started`);
      return;
    } catch (err) {
      console.error(
        `<FILE-API> | reportFileUploadCompleteCallback | ERROR Received following bucket information | ` +
          `%c${JSON.stringify(err)}`,
        "color:red;",
      );
      throw new Error(String(err));
    }
  };
};

export const useGetIntakeFileParseStatusRetriever: (
  fileUploadedCallback: () => void,
  setLearnerFileId: (learnerFileId: string | undefined) => void,
  onError?: () => void,
) => FileUploadAndParseProps["fileParseStatusRetriever"] = (fileUploadedCallback, setLearnerFileId, onError) => {
  const { getAccessTokenSilently } = useAuth0();
  return async (fileId: string) => {
    try {
      const token = await getAccessTokenSilently();
      const authHeaders: HeadersInit = new Headers();
      authHeaders.append("Authorization", `Bearer ${token}`);
      const headers = convertHeadersToObject(authHeaders);
      const response = await FronteraGraphQLClient.request(LearnerFileByIdDocument, { fileId }, headers);

      const learnerByFileIdResponse = response as LearnerFileByIdQuery | undefined;

      const apiStatus: ApiLearnerFileStatusChoices =
        learnerByFileIdResponse?.learnerFileById?.status || ("<missing-status" as ApiLearnerFileStatusChoices);

      if (apiStatus === ApiLearnerFileStatusChoices.ProcessError) {
        datadogRum.addError("file-parsing-failed", {
          fileMetadata: {
            fileId,
            apiStatus,
          },
        });
        datadogRum.addAction("file-parsing-failed", {
          fileMetadata: {
            fileId,
            apiStatus,
          },
        });
        onError?.();
      }
      if (apiStatus === ApiLearnerFileStatusChoices.ProcessSuccess) {
        fileUploadedCallback();
        setLearnerFileId(fileId);
        datadogRum.addAction("file-parsing-succeeded", {
          fileMetadata: {
            fileId,
            apiStatus,
          },
        });
      }
      console.debug(`<FILE-API> | fileParseStatusRetriever | Got the following parse status from FDM | ${apiStatus}`);

      const status = ApiStatusToFileStatus[apiStatus];

      return {
        fileId,
        status,
      };
    } catch (err) {
      console.error(
        `<FILE-API> | fileParseStatusRetriever | ERROR Received error trying to get parse status | ` +
          `%c${JSON.stringify(err)}`,
        "color:red;",
      );
      onError?.();
      throw new Error(String(err));
    }
  };
};

export const useGetIntakeRemoveFileCallback: (
  setLearnerFileId: (learnerFileId: string | undefined) => void,
) => FileUploadAndParseProps["removeFileCallback"] = (setLearnerFileId) => {
  const deleteLearnerFileMutation = useDeleteLearnerFileMutation({
    throwOnError: false,
  });

  return async (fileId: string) => {
    try {
      if (fileId && !fileId.startsWith("<")) {
        await deleteLearnerFileMutation.mutateAsync({ fileId });
        setLearnerFileId(undefined);
        notifySuccess(`Successfully deleted file`);
      }
    } catch (err) {
      console.error("Error with deleting file", err);
      notifyError(`Failed to delete file. Something went wrong on our end`);
    }
  };
};
